import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { marked } from 'marked';
import Header from '../../components/ui/Header';
import Sidebar from '../../components/ui/Sidebar';
import Breadcrumb from '../../components/ui/Breadcrumb';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import { useCurrentUser } from '../../lib/currentUser';

// Bundle the cheat sheet MD file into the app so it's not fetchable
// from GitHub (the source repo is private) and so demo visitors can't
// see this by URL-hacking to the raw file.
import cheatSheetMd from '../../../docs/DISTRICT_CHEAT_SHEET.md?raw';

marked.setOptions({ gfm: true, breaks: false });

// ---------------------------------------------------------------------------
// District Q&A cheat sheet — /cheat-sheet
//
// Route is gated: if the visitor isn't signed in with a real Supabase
// backend session (i.e. any demo visitor, or an unauthenticated URL-
// scraper), we render a friendly "Owner-only page" placeholder instead
// of the sheet. This is Jennifer's live-call playbook — it's not
// something we want a district evaluator running the demo to see, and
// it's not something we want indexed if the app ever goes public.
// ---------------------------------------------------------------------------

const CheatSheetPage = () => {
  const navigate = useNavigate();
  const currentUser = useCurrentUser();
  const isRealOwner = currentUser?.backend === 'supabase';

  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);

  const html = useMemo(() => {
    try {
      return marked.parse(cheatSheetMd || '');
    } catch (err) {
      console.error('Failed to render cheat sheet:', err);
      return '';
    }
  }, []);

  const handlePrint = () => {
    if (typeof window !== 'undefined' && typeof window.print === 'function') {
      window.print();
    }
  };

  useEffect(() => {
    if (!isRealOwner) return undefined;
    // Add a body class only in print mode so the cheat-sheet page
    // fills the whole page cleanly. We hide the app chrome via the
    // print:hidden utility on the wrapping divs; this belt-and-
    // suspenders class handles background-color quirks in Safari.
    document.body.classList.add('cheat-sheet-print-mode');
    return () => document.body.classList.remove('cheat-sheet-print-mode');
  }, [isRealOwner]);

  return (
    <div className="min-h-screen bg-background">
      <div className="print:hidden">
        <Header
          onMenuToggle={() => setIsSidebarOpen(!isSidebarOpen)}
          isMenuOpen={isSidebarOpen}
        />
        <Sidebar
          isOpen={isSidebarOpen}
          onClose={() => setIsSidebarOpen(false)}
          isCollapsed={isSidebarCollapsed}
          onToggleCollapse={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
        />
      </div>

      <main
        className={`transition-all duration-300 pt-16 print:pt-0 print:ml-0 ${
          isSidebarCollapsed ? 'lg:ml-16' : 'lg:ml-72'
        }`}
      >
        <div className="p-4 sm:p-6 print:p-0">
          <div className="max-w-4xl mx-auto space-y-4 print:max-w-full print:space-y-0">
            <div className="print:hidden">
              <Breadcrumb
                customItems={[
                  { label: 'Dashboard', path: '/dashboard', icon: 'LayoutDashboard' },
                  { label: 'District Cheat Sheet', path: null, icon: 'FileText', current: true },
                ]}
              />
            </div>

            {!isRealOwner ? (
              <section className="bg-card border border-border rounded-lg p-8 text-center">
                <Icon name="Lock" size={40} className="mx-auto text-primary mb-3" />
                <h1 className="text-xl font-semibold text-foreground">
                  Owner-only page
                </h1>
                <p className="text-sm text-muted-foreground max-w-md mx-auto mt-2">
                  This page is a private sales playbook for the owner
                  of this EduTracker Pro deployment. Please sign in with
                  a real owner account to view it.
                </p>
                <div className="mt-4 flex items-center justify-center gap-2 flex-wrap">
                  <Button
                    variant="default"
                    onClick={() => navigate('/login?live=1')}
                  >
                    <Icon name="LogIn" size={14} />
                    Sign in
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => navigate('/dashboard')}
                  >
                    Back to app
                  </Button>
                </div>
              </section>
            ) : (
              <>
                <div className="flex flex-wrap items-start justify-between gap-3 print:hidden">
                  <div>
                    <h1 className="text-2xl font-bold text-foreground">
                      District Q&amp;A Cheat Sheet
                    </h1>
                    <p className="text-sm text-muted-foreground max-w-2xl mt-1">
                      Your live-call playbook. Every question below is one
                      districts actually ask. Say the ✅ version. Never
                      say the ⚠️ version. When something new comes up in
                      a call, message me and I'll add it.
                    </p>
                  </div>
                  <Button
                    variant="outline"
                    onClick={handlePrint}
                    className="min-h-12"
                    title="Print this cheat sheet (or Save as PDF from the print dialog)"
                  >
                    <Icon name="Printer" size={16} />
                    Print / Save as PDF
                  </Button>
                </div>

                <section
                  className="bg-card border border-border rounded-lg p-6 sm:p-8 print:p-0 print:border-0 print:rounded-none print:bg-white"
                >
                  <div
                    className="prose prose-sm sm:prose-base max-w-none prose-headings:scroll-mt-4 prose-a:text-primary prose-blockquote:border-l-primary prose-blockquote:bg-primary/5 prose-blockquote:py-1 prose-blockquote:px-3 prose-blockquote:not-italic prose-blockquote:text-foreground"
                    dangerouslySetInnerHTML={{ __html: html }}
                  />
                </section>
              </>
            )}
          </div>
        </div>
      </main>

      <style>{`
        @media print {
          @page { size: letter; margin: 0.5in; }
          html, body { background: #fff !important; }
          .print\\:hidden { display: none !important; }
        }
      `}</style>
    </div>
  );
};

export default CheatSheetPage;
